import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Plus, Clock, Repeat, ArrowRightLeft, Scale, ChevronRight } from 'lucide-react';
import { Button, TableHeader, CardStats, SearchInput, Pagination, ExportDropdown } from '@components/ui';
import { TablePageSkeleton } from '@components/skeleton';
import { usePagination, useExportExcel } from '@hooks';
import { mockScheduledPayments, getScheduledPaymentsByConstructionId, addMockScheduledPayment } from '@mocks/planliOdemeler';
import { getCariStats, getCariAccountsByConstructionId } from '@mocks/cariHesaplar';
import PlanliOdemelerTablosu from './PlanliOdemelerTablosu';
import PlanliOdemeModal from './PlanliOdemeModal';
import CariHesapDetay from '@pages/Finans/CariHesaplar/CariHesapDetay';

export default function ScheduledPayments() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('debts'); // 'debts' veya 'expenses'
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCariAccount, setSelectedCariAccount] = useState(null);

    const allPayments = showAllConstructions
        ? mockScheduledPayments
        : getScheduledPaymentsByConstructionId(constructionId);

    // Sekmeye göre filtreleme (Demo amaçlı kategoriye göre ayırıyoruz)
    const tabFilteredPayments = allPayments.filter(payment => {
        if (activeTab === 'debts') {
            return ['Malzeme Alımı', 'Taşeron Hakedişi'].includes(payment.category); // Malzeme ve taşeronları borç sayalım
        } else {
            return !['Malzeme Alımı', 'Taşeron Hakedişi'].includes(payment.category); // Geri kalan sabit gider
        }
    });

    // Arama filtreleme
    const payments = tabFilteredPayments.filter(payment => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            payment.payee?.toLowerCase().includes(query) ||
            payment.category?.toLowerCase().includes(query) ||
            payment.description?.toLowerCase().includes(query)
        );
    });

    // Pagination
    const {
        paginatedData,
        currentPage,
        totalPages,
        totalItems,
        pageSize,
        goToPage,
        resetPage,
    } = usePagination(payments, 10);

    // Reset pagination when search or tab changes
    useEffect(() => {
        resetPage();
    }, [searchQuery, activeTab]);

    // Export
    const { exportToExcel, isExporting } = useExportExcel();

    const handleExportExcel = () => {
        const freqMap = { monthly: 'Aylık', 'one-time': 'Tek Seferlik', weekly: 'Haftalık' };
        const statusMap = { active: 'Aktif', scheduled: 'Planlandı', completed: 'Tamamlandı' };
        exportToExcel({
            data: payments.map(p => ({
                'Kategori': p.category,
                'Alıcı': p.payee,
                'Açıklama': p.description,
                'Tutar': p.amount,
                'Sıklık': freqMap[p.frequency] || p.frequency,
                'Sonraki Vade': p.nextDueDate,
                'Durum': statusMap[p.status] || p.status,
            })),
            columns: [
                { header: 'Kategori', key: 'Kategori' },
                { header: 'Alıcı', key: 'Alıcı' },
                { header: 'Açıklama', key: 'Açıklama' },
                { header: 'Tutar', key: 'Tutar', format: 'currency' },
                { header: 'Sıklık', key: 'Sıklık' },
                { header: 'Sonraki Vade', key: 'Sonraki Vade' },
                { header: 'Durum', key: 'Durum' },
            ],
            fileName: `odemeler_borclar_${activeTab}`,
            sheetName: activeTab === 'debts' ? 'Tedarikçi Borçları' : 'Sabit Giderler',
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    // İstatistikler (Sadece aktif sekmeye göre değil, totale göre)
    const totalDebts = allPayments
        .filter(p => ['Malzeme Alımı', 'Taşeron Hakedişi'].includes(p.category) && p.status !== 'completed')
        .reduce((sum, p) => sum + p.amount, 0);

    const monthlyExpenses = allPayments
        .filter(p => p.frequency === 'monthly' && p.status === 'active')
        .reduce((sum, p) => sum + p.amount, 0);

    const upcomingCount = allPayments.filter(p => p.status !== 'completed').length;

    // Cari Hesap özet istatistikleri (borçlar sekmesi için)
    const cariStats = getCariStats(constructionId);

    const handleAdd = () => {
        setSelectedPayment(null);
        setModalOpen(true);
    };

    const handleRowClick = (payment) => {
        setSelectedPayment(payment);
        setModalOpen(true);
    };

    const handleSubmit = (data) => {
        addMockScheduledPayment(data, constructionId);
        setModalOpen(false);
    };

    const [isLoading] = useState(false);
    if (isLoading) return <TablePageSkeleton statsCount={3} tableRows={10} tableColumns={6} />;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Başlık + Sekmeler aynı satırda */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Ödemeler ve Borçlar</h1>
                    <p className="text-text-tertiary mt-1">
                        {showAllConstructions
                            ? 'Tüm inşaatlar - Tedarikçi borçları ve sabit gider planlaması'
                            : `${selectedConstruction?.name || ''} - Borç ve ödeme takibi`}
                    </p>
                </div>

                {/* Sekmeler — başlıkla aynı hizada, sağda */}
                <div className="flex gap-1 p-1 bg-bg-secondary border border-border-light rounded-xl w-fit shrink-0">
                    <button
                        onClick={() => setActiveTab('debts')}
                        className={`cursor-pointer select-none px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'debts'
                            ? 'bg-bg-primary text-primary shadow-sm'
                            : 'text-text-secondary hover:text-text-primary hover:bg-bg-primary/50'
                            }`}
                    >
                        Tedarikçi / Taşeron Borçları
                    </button>
                    <button
                        onClick={() => setActiveTab('expenses')}
                        className={`cursor-pointer select-none px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'expenses'
                            ? 'bg-bg-primary text-primary shadow-sm'
                            : 'text-text-secondary hover:text-text-primary hover:bg-bg-primary/50'
                            }`}
                    >
                        Sabit Planlı Giderler
                    </button>
                </div>
            </div>

            {/* Stats — sekmeye göre dinamik */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {activeTab === 'debts' ? (
                    <>
                        <CardStats
                            title="Toplam Tedarikçi Borcu"
                            value={formatCurrency(totalDebts)}
                            icon={ArrowRightLeft}
                            color="error"
                            size="md"
                        />
                        <CardStats
                            title="Bekleyen Kayıt"
                            value={`${upcomingCount} adet`}
                            icon={Clock}
                            color="warning"
                            size="md"
                        />
                        <CardStats
                            title="Toplam Cari Bakiye"
                            value={formatCurrency(cariStats.totalBalance)}
                            icon={Scale}
                            color="error"
                            size="md"
                        />
                    </>
                ) : (
                    <>
                        <CardStats
                            title="Aylık Sabit Giderler"
                            value={formatCurrency(monthlyExpenses)}
                            icon={Repeat}
                            color="primary"
                            size="md"
                        />
                        <CardStats
                            title="Bekleyen Kayıt"
                            value={`${upcomingCount} adet`}
                            icon={Clock}
                            color="warning"
                            size="md"
                        />
                        <CardStats
                            title="Toplam Tedarikçi Borcu"
                            value={formatCurrency(totalDebts)}
                            icon={ArrowRightLeft}
                            color="error"
                            size="md"
                        />
                    </>
                )}
            </div>

            {/* Cari Hesap Özet Bandı — Borçlar sekmesinde göster */}
            {activeTab === 'debts' && cariStats.totalBalance > 0 && (
                <div className="flex items-center justify-between gap-4 px-4 py-3 bg-error/5 border border-error/20 rounded-xl">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-error/10 rounded-lg flex items-center justify-center shrink-0">
                            <Scale size={16} className="text-error" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-text-primary">
                                Toplam Açık Cari Bakiye:{' '}
                                <span className="text-error">
                                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(cariStats.totalBalance)}
                                </span>
                            </p>
                            <p className="text-xs text-text-tertiary mt-0.5">
                                {cariStats.activeCount} firmada açık bakiye — bu planlı borçların muhasebe kaydı Cari Hesaplarda tutulmaktadır.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/finans/cari')}
                        className="shrink-0 flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    >
                        Cari Hesaplar
                        <ChevronRight size={14} />
                    </button>
                </div>
            )}

            {/* Table Area */}
            <div className="bg-bg-primary rounded-xl border border-border-light overflow-hidden">
                <TableHeader
                    title={activeTab === 'debts' ? "Cari Borç Listesi" : "Planlı Gider Listesi"}
                    subtitle={showAllConstructions ? 'Tüm projeler' : selectedConstruction?.name}
                    className="border-none"
                >
                    <SearchInput
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={activeTab === 'debts' ? "Tedarikçi veya açıklama ara..." : "Gider kalemi ara..."}
                        size="md"
                    />
                    <ExportDropdown
                        onExportExcel={handleExportExcel}
                        isExporting={isExporting}
                    />
                    <Button
                        variant="primary"
                        icon={<Plus size={18} />}
                        onClick={handleAdd}
                    >
                        {activeTab === 'debts' ? 'Yeni Borç/Fatura Ekle' : 'Planlı Gider Ekle'}
                    </Button>
                </TableHeader>

                <PlanliOdemelerTablosu
                    data={paginatedData}
                    onRowClick={handleRowClick}
                    onCariClick={setSelectedCariAccount}
                    activeTab={activeTab}
                />

                {paginatedData.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        pageSize={pageSize}
                        onPageChange={goToPage}
                    />
                )}
            </div>

            <PlanliOdemeModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                payment={selectedPayment}
                activeTab={activeTab} // Modal'a hangi tabda olduğumuzu gönderiyoruz ki ona göre form açılsın
            />

            {/* Cari Hesap Detay - planlı borç satırındaki cari badge'e tıklanınca açılır */}
            {selectedCariAccount && (
                <CariHesapDetay
                    account={selectedCariAccount}
                    constructionId={constructionId}
                    onClose={() => setSelectedCariAccount(null)}
                    onMovementAdded={() => setSelectedCariAccount(null)}
                />
            )}
        </div>
    );
}
